type LoopKind = 'incoming' | 'outgoing';
type Note = { offset: number; duration: number; frequency: number; volume: number };

class CallRingtone {
	private context: AudioContext | null = null;
	private timer: ReturnType<typeof setInterval> | null = null;
	private timeout: ReturnType<typeof setTimeout> | null = null;
	private callId: string | null = null;
	private loopKind: LoopKind | null = null;
	private loopOutput: GainNode | null = null;
	private cueOutput: GainNode | null = null;
	private cueTimeout: ReturnType<typeof setTimeout> | null = null;
	private deadlines = new Map<string, number>();

	async unlock(): Promise<void> {
		if (typeof window === 'undefined' || !('AudioContext' in window)) return;
		try {
			this.context ??= new AudioContext();
			await this.context.resume();
			const gain = this.context.createGain();
			gain.gain.value = 0.00001;
			gain.connect(this.context.destination);
			const oscillator = this.context.createOscillator();
			oscillator.connect(gain);
			oscillator.start();
			oscillator.stop(this.context.currentTime + 0.01);
			if (this.loopKind && !this.loopOutput) this.ring(this.loopKind);
		} catch {
			// iOS разрешит звук после следующего осознанного нажатия пользователя.
		}
	}

	setDeadline(callId: string, timeoutSeconds: number): void {
		const deadline = Date.now() + Math.max(1, timeoutSeconds) * 1000;
		this.deadlines.set(callId, deadline);
		if (this.callId === callId && this.loopKind === 'incoming') this.scheduleStop(deadline);
	}

	start(callId: string): void {
		this.startLoop(callId, 'incoming');
	}

	startOutgoing(callId: string): void {
		this.startLoop(callId, 'outgoing');
	}

	stop(callId?: string): void {
		if (callId && this.callId !== callId) return;
		if (this.timer) clearInterval(this.timer);
		if (this.timeout) clearTimeout(this.timeout);
		this.timer = null;
		this.timeout = null;
		this.callId = null;
		this.loopKind = null;
		this.disconnect(this.loopOutput);
		this.loopOutput = null;
	}

	cancel(callId: string): void {
		this.stop(callId);
		this.stopCue();
	}

	connected(callId: string): void {
		this.stop(callId);
		this.playCue([
			{ offset: 0, duration: 0.11, frequency: 660, volume: 0.07 },
			{ offset: 0.13, duration: 0.14, frequency: 880, volume: 0.07 }
		]);
	}

	reconnecting(): void {
		this.playCue([
			{ offset: 0, duration: 0.12, frequency: 480, volume: 0.07 },
			{ offset: 0.17, duration: 0.12, frequency: 420, volume: 0.07 }
		]);
	}

	reconnected(): void {
		this.playCue([{ offset: 0, duration: 0.16, frequency: 740, volume: 0.07 }]);
	}

	unavailable(callId: string): void {
		this.stop(callId);
		this.playCue([
			{ offset: 0, duration: 0.12, frequency: 560, volume: 0.07 },
			{ offset: 0.16, duration: 0.18, frequency: 420, volume: 0.07 }
		]);
	}

	interrupted(callId: string): void {
		this.stop(callId);
		this.playCue([
			{ offset: 0, duration: 0.13, frequency: 620, volume: 0.08 },
			{ offset: 0.15, duration: 0.22, frequency: 390, volume: 0.08 }
		]);
	}

	private startLoop(callId: string, kind: LoopKind): void {
		if (this.callId === callId && this.loopKind === kind && this.timer) return;
		this.stop();
		this.stopCue();
		const deadline =
			kind === 'incoming'
				? (this.deadlines.get(callId) ?? Date.now() + 60_000)
				: Date.now() + 300_000;
		if (deadline <= Date.now()) return;
		this.callId = callId;
		this.loopKind = kind;
		this.ring(kind);
		this.timer = setInterval(() => this.ring(kind), kind === 'incoming' ? 2_700 : 5_000);
		this.scheduleStop(deadline);
	}

	private scheduleStop(deadline: number): void {
		if (this.timeout) clearTimeout(this.timeout);
		this.timeout = setTimeout(() => this.stop(), Math.max(0, deadline - Date.now()));
	}

	private ring(kind: LoopKind): void {
		const context = this.context;
		if (!context || context.state !== 'running') return;
		if (!this.loopOutput) {
			this.loopOutput = context.createGain();
			this.loopOutput.connect(context.destination);
		}
		const output = this.loopOutput;
		if (kind === 'incoming') {
			this.tone(output, { offset: 0, duration: 0.34, frequency: 720, volume: 0.16 });
			this.tone(output, { offset: 0.48, duration: 0.34, frequency: 880, volume: 0.16 });
		} else {
			this.tone(output, { offset: 0, duration: 1, frequency: 425, volume: 0.09 });
		}
	}

	private playCue(notes: Note[]): void {
		this.stopCue();
		const context = this.context;
		if (!context || context.state !== 'running') return;
		const output = context.createGain();
		output.connect(context.destination);
		this.cueOutput = output;
		for (const note of notes) this.tone(output, note);
		const duration = Math.max(...notes.map((note) => note.offset + note.duration));
		this.cueTimeout = setTimeout(() => this.stopCue(), (duration + 0.05) * 1000);
	}

	private stopCue(): void {
		if (this.cueTimeout) clearTimeout(this.cueTimeout);
		this.cueTimeout = null;
		this.disconnect(this.cueOutput);
		this.cueOutput = null;
	}

	private disconnect(output: GainNode | null): void {
		if (!output) return;
		output.gain.value = 0;
		output.disconnect?.();
	}

	private tone(output: GainNode, note: Note): void {
		const context = this.context;
		if (!context) return;
		const oscillator = context.createOscillator();
		const gain = context.createGain();
		const start = context.currentTime + note.offset;
		const end = start + note.duration;
		oscillator.type = 'sine';
		oscillator.frequency.value = note.frequency;
		gain.gain.setValueAtTime(0.0001, start);
		gain.gain.exponentialRampToValueAtTime(note.volume, start + 0.025);
		gain.gain.exponentialRampToValueAtTime(0.0001, end);
		oscillator.connect(gain);
		gain.connect(output);
		oscillator.start(start);
		oscillator.stop(end + 0.02);
	}
}

export const callRingtone = new CallRingtone();
